import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Users, Eye, Edit3, Check } from 'lucide-react';
import { useTeams } from '../../hooks/useTeams';
import { useAuth } from '../../hooks/useAuth';
import type { TeamPermission, TeamSermonShare } from '../../types/teams';

interface TeamShareModalProps {
  sermonId: string;
  sermonTitle: string;
  onClose: () => void;
}

export function TeamShareModal({ sermonId, sermonTitle, onClose }: TeamShareModalProps) {
  const { user } = useAuth();
  const { teams, loading: teamsLoading, getSermonShares, shareSermonWithTeam, updateSermonSharePermission, unshareSermonFromTeam } = useTeams();
  const [existingShares, setExistingShares] = useState<TeamSermonShare[]>([]);
  const [selections, setSelections] = useState<Record<string, TeamPermission | null>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const initializedRef = useRef(false);

  const ownedTeams = teams.filter(t => t.created_by === user?.id);

  // Load shares and pre-populate selections once ownedTeams is available.
  // Depends on ownedTeams.length so it re-runs when teams finish loading (0 → N),
  // but the initializedRef guard prevents wiping the user's selections on any
  // subsequent teams reload.
  useEffect(() => {
    // Still waiting for useTeams to finish — keep spinner going
    if (teamsLoading) return;

    // Teams loaded but user owns none — stop spinner so "No teams yet" shows
    if (ownedTeams.length === 0) {
      setLoading(false);
      return;
    }

    // Already initialized — don't overwrite the user's in-progress selections
    if (initializedRef.current) return;
    initializedRef.current = true;

    const load = async () => {
      setLoading(true);
      const shares = await getSermonShares(sermonId);
      setExistingShares(shares);
      const initial: Record<string, TeamPermission | null> = {};
      for (const team of ownedTeams) {
        const existing = shares.find(s => s.team_id === team.id);
        initial[team.id] = existing ? existing.permission : null;
      }
      setSelections(initial);
      setLoading(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sermonId, teamsLoading, ownedTeams.length]);

  const handleToggle = (teamId: string, permission: TeamPermission) => {
    setSelections(prev => {
      const current = prev[teamId];
      // Clicking the same permission again deselects (unshare)
      if (current === permission) return { ...prev, [teamId]: null };
      return { ...prev, [teamId]: permission };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const team of ownedTeams) {
        // Guard: skip teams whose selection was never initialized (undefined ≠ null)
        if (!(team.id in selections)) continue;
        const desired = selections[team.id] as TeamPermission | null;
        const existing = existingShares.find(s => s.team_id === team.id);

        if (desired === null && existing) {
          await unshareSermonFromTeam(sermonId, team.id);
        } else if (desired !== null && !existing) {
          await shareSermonWithTeam(sermonId, team.id, desired);
        } else if (desired !== null && existing && desired !== existing.permission) {
          // Use a targeted UPDATE rather than upsert when changing an existing share
          await updateSermonSharePermission(sermonId, team.id, desired);
        }
      }

      setSaved(true);
      setTimeout(() => onClose(), 800);
    } catch (e: any) {
      setError(e?.message || 'Failed to save sharing settings');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = ownedTeams.some(team => {
    if (!(team.id in selections)) return false; // not yet initialized
    const desired = selections[team.id];
    const existing = existingShares.find(s => s.team_id === team.id);
    const existingPerm = existing?.permission ?? null;
    return desired !== existingPerm;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-sand/20">
          <div>
            <h2 className="text-xl font-semibold text-midnight">Share with Team</h2>
            <p className="text-sm text-slate mt-0.5 truncate max-w-xs">"{sermonTitle}"</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-crimson/10 text-crimson rounded-lg text-sm">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-electric" />
            </div>
          ) : ownedTeams.length === 0 ? (
            <div className="text-center py-8 text-slate">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-midnight">No teams yet</p>
              <p className="text-sm mt-1">Create a team first from the Teams page</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate mb-4">
                Select which teams can access this sermon and what they can do.
              </p>
              {ownedTeams.map(team => {
                const selected = selections[team.id];
                return (
                  <div key={team.id} className="border border-sand/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="bg-electric/10 p-1.5 rounded">
                        <Users className="h-4 w-4 text-electric" />
                      </div>
                      <span className="font-medium text-midnight text-sm">{team.name}</span>
                      <span className="text-xs text-slate">
                        {team.members?.filter(m => m.status === 'accepted').length || 0} member{(team.members?.filter(m => m.status === 'accepted').length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {/* No access */}
                      <button
                        onClick={() => setSelections(prev => ({ ...prev, [team.id]: null }))}
                        className={`py-2 px-3 rounded-lg border-2 text-xs font-medium transition-colors ${
                          selected === null
                            ? 'border-gray-400 bg-gray-100 text-gray-700'
                            : 'border-sand/20 text-slate hover:border-sand/40'
                        }`}
                      >
                        No Access
                      </button>
                      {/* Viewer */}
                      <button
                        onClick={() => handleToggle(team.id, 'viewer')}
                        className={`py-2 px-3 rounded-lg border-2 text-xs font-medium transition-colors flex flex-col items-center gap-1 ${
                          selected === 'viewer'
                            ? 'border-electric bg-electric/5 text-electric'
                            : 'border-sand/20 text-slate hover:border-sand/40'
                        }`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Viewer
                      </button>
                      {/* Editor */}
                      <button
                        onClick={() => handleToggle(team.id, 'editor')}
                        className={`py-2 px-3 rounded-lg border-2 text-xs font-medium transition-colors flex flex-col items-center gap-1 ${
                          selected === 'editor'
                            ? 'border-electric bg-electric/5 text-electric'
                            : 'border-sand/20 text-slate hover:border-sand/40'
                        }`}
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Editor
                      </button>
                    </div>
                    {selected === 'editor' && (
                      <p className="text-xs text-amber-600 mt-2">
                        Editors can modify content and create slides. They cannot delete or reshare the sermon.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-sand/20">
          <button onClick={onClose} className="px-4 py-2 text-slate hover:text-midnight">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges || saved || ownedTeams.length === 0}
            className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 flex items-center gap-2 min-w-[80px] justify-center"
          >
            {saved ? (
              <><Check className="h-4 w-4" /> Saved</>
            ) : saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
